import { useEffect, useState } from 'react';
import { Button, Empty, Table, TableProps } from 'antd';
import { Link } from 'react-router-dom';
import { PlusOutlined } from '@ant-design/icons';

// services
import NotificationService from 'core/services/notification';
import { onDeleteDistrict, onGetDistricts } from 'modules/districts/services/districts';

// models
import { District } from 'modules/districts/models/district';

// components
import ConfirmModalComponent from 'components/confirmModal/confirmModal.component';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import Icon from 'utils/icon';
import AuthActionComponent from 'components/authAction/authAction';
import NoData from 'components/noData/noData';
import { useTranslation } from 'react-i18next';
import { isRTL } from './../../../../core/services/language';

function ListDistrictsPage() {
	const { t } = useTranslation();

	const [isLoading, setIsLoading] = useState(true);
	const [districts, setDistricts] = useState<District[]>([]);
	const [isDeleting, setIsDeleting] = useState(false);
	const [showConfirmation, setShowConfirmation] = useState(false);
	const [tempDeletedDistrict, setTempDeletedDistrict] = useState<number>();

	// pagination states
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [totalRecords, setTotalRecords] = useState<number>(0);
	const [isLoadMore, setIsLoadMore] = useState(false);

	const columns: TableProps<District>['columns'] = [
		{
			title: t('DISTRICTS.NAME'),
			dataIndex: 'name',
			key: 'name',
			render: (name) => <p className="mb-0 fw-medium">{isRTL() ? name.ar : name.en}</p>,
		},
		{
			title: t('COMMON.STATUS'),
			dataIndex: 'active',
			key: 'active',
			render: (active) => (
				<span className={`status fw-medium ${active ? 'active' : 'inactive'}`}>{active ? t('COMMON.ACTIVE') : t('COMMON.INACTIVE')}</span>
			),
		},
		{
			title: t('COMMON.ACTIONS'),
			dataIndex: '',
			key: '',
			render: (_, district) => (
				<div className="d-flex gap-2">
					<AuthActionComponent action="district_update">
						<Link to={`${district.id}`}>
							<Icon name="edit" width={35} height={35} />
						</Link>
					</AuthActionComponent>
					<AuthActionComponent action="district_delete">
						<Button type="link" className="p-0" onClick={() => onShowConfirmation(district.id)}>
							<Icon name="delete" width={35} height={35} />
						</Button>
					</AuthActionComponent>
				</div>
			),
		},
	];

	useEffect(() => {
		getDistricts();
	}, []);

	const getDistricts = async () => {
		try {
			setIsLoading(true);

			const response = await onGetDistricts();
			setDistricts(response.data.data.districts);
			setTotalRecords(response.data.data.pagination.total);

			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.OPS'), t('COMMON.ERROR_OCCURRED'));
			setIsLoading(false);
		}
	};

	const fetchMoreData = async (page: number) => {
		try {
			setIsLoadMore(true);
			window.scrollTo({ behavior: 'smooth', top: 0 });
			const response = await onGetDistricts(page);
			setDistricts(response.data.data.districts);
			setCurrentPage(page);
			setIsLoadMore(false);
		} catch (error) {
			NotificationService.error(t('COMMON.OPS'), t('COMMON.ERROR_OCCURRED'));
			setIsLoadMore(false);
		}
	};

	const onShowConfirmation = (districtId: number) => {
		setShowConfirmation(true);
		setTempDeletedDistrict(districtId);
	};

	const onDiscardConfirmation = () => {
		setShowConfirmation(false);
	};

	const onDelete = async () => {
		try {
			setIsDeleting(true);
			await onDeleteDistrict(tempDeletedDistrict as number);
			NotificationService.success(t('COMMON.SUCCESS'), t('DISTRICTS.DELETED_SUCCESS'));
			setShowConfirmation(false);
			setIsDeleting(false);
			getDistricts();
		} catch (error) {
			setIsDeleting(false);
			setShowConfirmation(false);
			NotificationService.error(t('COMMON.OPS'), t('DISTRICTS.DELETE_ERROR'));
		}
	};

	return (
		<>
			<div className="pageHeader">
				<h3>{t('DISTRICTS.MANAGEMENT_TITLE')}</h3>
			</div>

			{isLoading ? (
				<LoadingComponent />
			) : (
				<div className={`sectionContainer listData`}>
					<header>
						<div>
							<h5>
								{totalRecords} {t('DISTRICTS.MANAGEMENT_COUNT')}
							</h5>
							<p>{t('DISTRICTS.MANAGEMENT_DESCRIPTION')}</p>
						</div>
						<AuthActionComponent action="district_create">
							<Link to="create">
								<Button type="primary" icon={<PlusOutlined />} style={{ background: '#F1F1F2', color: '#7E8299', border: 'none' }}>
									{t('COMMON.ADD')}
								</Button>
							</Link>
						</AuthActionComponent>
					</header>

					<div style={{ position: 'relative' }}>
						<Table
							columns={columns}
							dataSource={districts}
							scroll={{ x: 600 }}
							rowKey="id"
							pagination={{
								pageSize: 15,
								total: totalRecords,
								showSizeChanger: false,
								current: currentPage,
								onChange: fetchMoreData,
							}}
							locale={{ emptyText: <NoData /> }}
						/>

						{isLoadMore && (
							<div className="loadMoreContainer">
								<LoadingComponent />
							</div>
						)}
					</div>
				</div>
			)}

			<ConfirmModalComponent
				open={showConfirmation}
				confirmText={t('COMMON.DELETE')}
				onDiscard={onDiscardConfirmation}
				title={t('DISTRICTS.DELETE_TITLE')}
				description={t('DISTRICTS.DELETE_DESCRIPTION')}
				note={t('DISTRICTS.DELETE_NOTE')}
				onConfirm={onDelete}
				isLoading={isDeleting}
			/>
		</>
	);
}

export default ListDistrictsPage;
